import { defineStore } from "pinia";
import { ref } from "vue";
import { cursorPagerStoreFactory } from "@/stores/factory/list-store-factory";
import { entityStoreFactory } from "@/stores/factory/entity-store-factory";
import {
  readTextImageExplorePage,
  readTextImageSearchPage,
} from "@/apis/explore";
import { TextImageExploreFilter } from "@/types/explore";
import { TextImage } from "@/types/entity1";
import { isMobile } from "@/modules/common";
import { TextImageModeEnum } from "@/enums/TextImageEnum";
import { useReply } from "@/stores/reply";
import { useSearch } from "@/stores/search";
import { SearchModeEnum, SorterEnum } from "@/enums";
import { PageResponse } from "@/types/api-types";

export const PAGE_SIZE = isMobile() ? 10 : 30;

export const cursorEncoder = (
  entity: TextImage,
  pointsToNextItems: boolean
) => {
  return btoa(
    JSON.stringify({
      id: entity.id,
      reply_emoji_count: entity.reply.emojiCount,
      _pointsToNextItems: pointsToNextItems,
    })
  );
};
export const useExplore = defineStore("explore", () => {
  const entityState = entityStoreFactory<TextImage>("id");
  const { updateMap } = entityState;
  const replyStore = useReply();
  const scrollY = ref(0);

  const exploreParam = ref<TextImageExploreFilter>({});

  const {
    state: txtImageExploreState,
    dataList: txtImageExploreList,
    fetchNewerList: fetcherNewerTxtImageExploreList,
    fetchOlderList: fetchOlderTxtImageExploreList,
    initListIfAbsent: initTxtImageExploreListIfAbsent,
    resetState: resetTextImageExploreData,
  } = cursorPagerStoreFactory({
    entityStore: entityState,
    fetch: async (cursor?: string) => {
      const res = await readTextImageExplorePage({
        tab: "hot",
        filter: exploreParam.value,
        pagination: { perPage: PAGE_SIZE },
        cursor,
      });
      const validData = res.page.data.filter((item) => item.media?.url);
      return updateEntityMap(validData);
    },
    cursorEncoder,
  });

  const {
    state: replaceModeState,
    dataList: replaceModeList,
    fetchNewerList: fetcherNewerReplaceModeList,
    fetchOlderList: fetchOlderReplaceModeList,
    initListIfAbsent: initReplaceModeListIfAbsent,
    resetState: resetReplaceModeState,
  } = cursorPagerStoreFactory({
    entityStore: entityState,
    fetch: async (cursor?: string) => {
      const res = await readTextImageExplorePage({
        tab: "hot",
        filter: {
          mode: TextImageModeEnum.ReplaceCharacter,
        },
        pagination: { perPage: 10 },
        cursor,
      });
      const validData = res.page.data.filter((item) => item.media?.url);
      return updateEntityMap(validData);
    },
    cursorEncoder,
  });

  // const searchParams = ref<TextImageExploreParam>({});
  const searchText = ref("");
  const searchScrollY = ref(0);
  const pageNo = ref(1);
  const searchTotalCount = ref(0);
  const prevSearchText = ref("");
  const searchFilter = ref<TextImageExploreFilter>({});
  const searchSorter = ref<SorterEnum>(SorterEnum.HOT);
  const {
    state: searchState,
    dataList: searchList,
    fetchOlderList: fetchOlderSearchList,
    initListIfAbsent: initSearchListIfAbsent,
    resetState: resetSearchState,
  } = cursorPagerStoreFactory({
    entityStore: entityState,
    fetch: async (cursor?: string) => {
      const res = (await readTextImageSearchPage({
        tab: searchSorter.value,
        filter: { ...searchFilter.value, query: searchText.value },
        pagination: { perPage: PAGE_SIZE, page: pageNo.value },
        cursor,
      })) as unknown as PageResponse<TextImage>;
      pageNo.value = pageNo.value + 1;
      searchTotalCount.value = res.page.total!;
      return updateEntityMap(res.page.data);
    },
    cursorEncoder,
  });

  function disableCache(): void {
    resetTextImageExploreData();
    resetReplaceModeState();
  }

  async function search(searchMode: SearchModeEnum) {
    pageNo.value = 1;
    searchTotalCount.value = 0;
    prevSearchText.value = searchText.value;
    const searchStore = useSearch();
    if (searchMode === SearchModeEnum.CHARACTER) {
      await searchStore.initMateListIfEmpty(searchText.value);
    } else if (searchMode === SearchModeEnum.MOMENT) {
      await searchStore.initMomentListIfEmpty(searchText.value);
    } else {
      resetSearchState();
      await initSearchListIfAbsent(true);
    }
  }

  function updateEntityMap(dataList: TextImage[]) {
    const items = updateMap(dataList);
    replyStore.updateMap(items.map((item) => item.reply));
    items.forEach((item) => {
      if (replyStore.getReply(item.reply.id)) {
        item.reply = replyStore.getReply(item.reply.id)!;
      }
    });
    return items;
  }

  return {
    // State.Explore
    txtImageExploreList,
    replaceModeList,
    searchList,
    scrollY,
    // State.Search
    searchScrollY,
    searchText,
    searchTotalCount,
    prevSearchText,
    searchFilter,
    searchSorter,
    // Actions
    fetcherNewerTxtImageExploreList,
    fetchOlderTxtImageExploreList,
    initTxtImageExploreListIfAbsent,
    updateEntityMap,
    entityState,

    initReplaceModeListIfAbsent,

    fetchOlderSearchList,
    initSearchListIfAbsent,
    disableCache,
    search,
  };
});
